import React, { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import ReactTooltip from 'react-tooltip'
import withApiData from '../HOC/withApiData'
import colors from '../../configuration/colors'
import { hideModal } from '../../actions/modal'
import Modal from '../mini/Modal'
import Button from '../mini/Buttons/Button'
import H2 from '../typography/headers/H2'
import MarginRight15 from '../typography/margins/MarginRight15'
import CancelButton from '../mini/Buttons/CancelButton'
import {
  deleteNewResource,
  fileUploadToResource,
  updateResource,
  clearResourceFile
} from '../mycourses/Activities/API'
import SelectorComponent from './CreateOrEditCourseModal/InputComponents/SelectorComponent'
import InputLabel from '../typography/Input/InputLabel'
import TextInputComponent from './CreateOrEditCourseModal/InputComponents/TextInputComponent'
import { isValidLink } from '../../utils/activityedit'
import { urlInvalid } from '../../utils/messages'
import { italicsOnlyMDEOptions } from '../../utils/markdowneditor'
import S3Uploader from '../activityedit/S3Uploader'
import FileTypeIcon from '../mini/FileTypeIcon'

const ModalContainer = styled.div`
  width: 660px;
  border-radius: 10px;
  box-shadow: 0 2px 3.8px 0.2px rgba(0, 0, 0, 0.15);
  background-color: ${colors.smoke};
  margin: 75px auto;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 0 30px 30px;
`

const AsteriskSymbol = styled.span`
  color: ${colors.crimson};
  padding-left: 3px;
`

const AddResourceModal = ({
  apiData,
  levelId,
  activityQuestId,
  resource,
  resourceId,
  resourceName
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [originalFileName, setOriginalFileName] = useState(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const S3UploaderRef = useRef(null)

  const [isLinkChecked, setIsLinkChecked] = useState(true)
  const handleIsLinkChecked = e => {
    if (e === false) {
      setUrlResource(null)
    } else {
      handleClearFile()
    }
    setIsLinkChecked(e)
  }

  const [resourceNameState, setResourceNameState] = useState(resourceName)
  const handleResourceName = e => {
    setResourceNameState(e.target.value)
  }

  const [resourceDescription, setResourceDescription] = useState(null)
  const handleResourceDescription = e => {
    let event = {
      target: {}
    }
    event.target.id = 'resource-description'
    event.target.value = e

    setResourceDescription(event.target.value)
  }

  const [urlResource, setUrlResource] = useState(null)
  const handleUrlResource = e => {
    setUrlResource(e.target.value)
  }

  const [selectedResourceType, setSelectedResourceType] = useState(null)
  const handleSelectedResourceType = e => {
    setSelectedResourceType(e)
  }

  const deleteMutation = useMutation(
    ({ apiData, levelId, activityQuestId, resourceId }) =>
      deleteNewResource(apiData, levelId, activityQuestId, resourceId)
  )
  const closeTheModal = () => {
    handleClearFile()
    deleteMutation.mutate(
      {
        apiData,
        levelId,
        activityQuestId,
        resourceId
      },
      {
        onSuccess: () => {
          dispatch(hideModal())
        }
      }
    )
  }

  const createMutationSaveUrlResource = useMutation(
    ({ apiData, levelId, activityQuestId, resourceId, resource }) =>
      updateResource(apiData, levelId, activityQuestId, resourceId, resource)
  )

  const handleSaveUrlResource = () => {
    const resource = {
      name: resourceNameState,
      description:
        resourceDescription === null || resourceDescription.length === 0
          ? null
          : resourceDescription,
      url: urlResource !== null ? urlResource : null,
      resource_type:
        selectedResourceType !== null && selectedResourceType.label !== 'App'
          ? selectedResourceType.label.toLowerCase()
          : 'game_app',
      new_resource: true
    }

    createMutationSaveUrlResource.mutate(
      {
        apiData,
        levelId,
        activityQuestId,
        resourceId,
        resource
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('resources-' + activityQuestId)
          setTimeout(() => {
            dispatch(hideModal())
          }, 300)
        }
      }
    )
  }

  const clearFileMutation = useMutation(
    ({ apiData, levelId, activityQuestId, resourceId }) =>
      clearResourceFile(apiData, levelId, activityQuestId, resourceId)
  )

  const handleClearFile = () => {
    clearFileMutation.mutate(
      {
        apiData,
        levelId,
        activityQuestId,
        resourceId,
        resource
      },
      {
        onSuccess: () => {
          setOriginalFileName(null)
        }
      }
    )
  }

  const couldSaveData =
    ((urlResource !== null && urlResource.length !== 0) ||
      originalFileName !== null) &&
    selectedResourceType !== null

  const resourceTypeOptions = [
    { id: 1, name: 'Audio', label: 'Audio', sort_order: 0 },
    { id: 2, name: 'Document', label: 'Document', sort_order: 1 },
    { id: 3, name: 'App', label: 'App', sort_order: 2 },
    { id: 4, name: 'Image', label: 'Image', sort_order: 3 },
    { id: 5, name: 'Presentation', label: 'Presentation', sort_order: 4 },
    { id: 6, name: 'Video', label: 'Video', sort_order: 5 },
    { id: 7, name: 'Web', label: 'Web', sort_order: 6 },
    { id: 8, name: 'Simulation', label: 'Simulation', sort_order: 7 }
  ]

  const onUploadFileStart = () => {
    setIsUploadingFile(true)
  }

  const onUploadFileEnd = () => {
    setIsUploadingFile(false)
  }

  const onUploadAbort = () => {
    setIsUploadingFile(false)
  }

  const onUploadComplete = () => {
    setIsUploadingFile(false)
  }

  const fileUploadMutation = useMutation(
    ({ apiData, levelId, activityQuestId, resourceId, file }) =>
      fileUploadToResource(apiData, levelId, activityQuestId, resourceId, file)
  )

  const patchFileUploadInfo = uploadedFile => {
    const file = {
      resource_file: uploadedFile.name,
      original_file_name: uploadedFile.original_file_name,
      file_size: uploadedFile.size,
      content_type: uploadedFile.type,
      file_checksum: uploadedFile.file_checksum
    }

    setOriginalFileName(uploadedFile.original_file_name)

    fileUploadMutation.mutate({
      apiData,
      levelId,
      activityQuestId,
      resourceId,
      file
    })
  }

  const resourceDescriptionOptions = Object.assign({}, italicsOnlyMDEOptions)
  resourceDescriptionOptions.initialValue = resourceDescription
  resourceDescriptionOptions.placeholder = 'Resource Description'
  resourceDescriptionOptions.autofocus = false

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        {isLoading ? (
          <div className="loading" />
        ) : (
          <React.Fragment>
            <HeaderContainer>
              <H2>+ Add Resource</H2>
              <FlexContainer style={{ marginBottom: '0px' }}>
                <MarginRight15>
                  <CancelButton
                    handleClick={closeTheModal}
                    title="Cancel"
                    width="104px"
                  />
                </MarginRight15>
                <div
                  data-for="createCourseTooltip"
                  data-tip="Please enter all required fields prior to saving this resource."
                >
                  <Button
                    width={isUploadingFile ? '165px' : '104px'}
                    title={isUploadingFile ? 'Upload In Progress' : 'Save'}
                    handleClick={handleSaveUrlResource}
                    buttonColor={colors.oeYellow}
                    borderColor={colors.oeYellow}
                    hoverBackground={colors.charcoal4}
                    hoverColor={colors.white}
                    couldSaveData={
                      isLinkChecked
                        ? isValidLink(urlResource) && couldSaveData
                        : !isUploadingFile && couldSaveData
                    }
                    shouldShowTooltipAndDisableButton={true}
                  />
                  {!couldSaveData && (
                    <ReactTooltip
                      id="createCourseTooltip"
                      place="bottom"
                      effect="solid"
                      className="activityCourseTooltip"
                    />
                  )}
                </div>
              </FlexContainer>
            </HeaderContainer>
            <MainContainer>
              <TextInputComponent
                title="Resource Name"
                placeholder="Resource Name"
                handleEnteredTextValue={handleResourceName}
                value={resourceNameState}
                shouldDisplayAsteriskSymbol={true}
                shouldBeTextarea={false}
              />
              <TextInputComponent
                title="Resource Description"
                placeholder="Resource Description"
                handleEnteredTextValue={handleResourceDescription}
                value={resourceDescription}
                shouldDisplayAsteriskSymbol={false}
                shouldBeTextarea={true}
                options={resourceDescriptionOptions}
              />
              <SelectorComponent
                placeholder="Resource Type"
                handleSelectedValueChange={handleSelectedResourceType}
                selectedValue={selectedResourceType}
                options={resourceTypeOptions}
                shouldDisplayAsterisk={true}
              />
              <div>
                <InputLabel>Format</InputLabel>
                <FlexContainer>
                  <div style={{ marginRight: '30px' }}>
                    <input
                      type="radio"
                      id="Link"
                      name="Link"
                      value="Link"
                      style={{ marginRight: '10px' }}
                      checked={isLinkChecked}
                      onChange={() => handleIsLinkChecked(true)}
                    />
                    <label htmlFor="Link">Link</label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="File"
                      name="File"
                      value="File"
                      style={{ marginRight: '10px' }}
                      checked={!isLinkChecked}
                      onChange={() => handleIsLinkChecked(false)}
                    />
                    <label htmlFor="File">File</label>
                  </div>
                </FlexContainer>
              </div>
              {isLinkChecked ? (
                <div>
                  <TextInputComponent
                    title="URL"
                    placeholder="URL"
                    handleEnteredTextValue={handleUrlResource}
                    value={urlResource}
                    shouldDisplayAsteriskSymbol={true}
                    shouldBeTextarea={false}
                    isError={
                      urlResource !== null &&
                      urlResource.length !== 0 &&
                      !isValidLink(urlResource)
                    }
                    errorMessage={urlInvalid}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '20px' }}>
                  <InputLabel style={{ marginTop: '0px' }}>
                    Resource File
                    <AsteriskSymbol>*</AsteriskSymbol>
                  </InputLabel>
                  <div>
                    {originalFileName !== null ? (
                      <FlexContainer style={{ marginTop: '20px' }}>
                        <FileTypeIcon
                          type={
                            selectedResourceType === null
                              ? 'document'
                              : selectedResourceType.label !== 'App'
                              ? selectedResourceType.label.toLowerCase()
                              : 'game_app'
                          }
                        />
                        <div>{originalFileName}</div>
                        <span
                          style={{
                            marginLeft: '15px',
                            marginTop: '4px',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={() => setIsHovered(true)}
                          onMouseLeave={() => setIsHovered(false)}
                          onClick={handleClearFile}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 25 24"
                          >
                            <g
                              fill="none"
                              fillRule="evenodd"
                              stroke={
                                isHovered ? colors.crimsonLite : colors.dove
                              }
                              strokeWidth="3"
                              transform="translate(2 1)"
                            >
                              <path d="M0 0L22 22M22 0L0 22" />
                            </g>
                          </svg>
                        </span>
                      </FlexContainer>
                    ) : (
                      <React.Fragment>
                        <S3Uploader
                          ref={S3UploaderRef}
                          presignedUrl={resource.presigned_url}
                          uniqueFilename={resource.unique_upload_file_name}
                          patchFileUploadInfo={patchFileUploadInfo}
                          resourceType={
                            selectedResourceType !== null &&
                            selectedResourceType.label.toLowerCase()
                          }
                          resourceId={resourceId}
                          uploadStartHandler={onUploadFileStart}
                          uploadEndHandler={onUploadFileEnd}
                          uploadAbortHandler={onUploadAbort}
                          uploadCompleteHandler={onUploadComplete}
                          isActivityCourse={true}
                        />
                      </React.Fragment>
                    )}
                  </div>
                </div>
              )}
            </MainContainer>
          </React.Fragment>
        )}
      </ModalContainer>
    </Modal>
  )
}

AddResourceModal.propTypes = {
  apiData: PropTypes.object.isRequired,
  levelId: PropTypes.string.isRequired,
  activityQuestId: PropTypes.string.isRequired,
  resource: PropTypes.object.isRequired,
  resourceId: PropTypes.number.isRequired,
  resourceName: PropTypes.string.isRequired
}

export default withApiData(AddResourceModal)



// WEBPACK FOOTER //
// ./src/components/modal/AddResourceModal.js